import { useMemo, useState } from "react";
import ChartTooltip from "./ChartTooltip.jsx";
import { MUSCLE_BUCKETS } from "../utils/analytics.js";
import { deltaSign } from "../utils/format.js";
import styles from "./HexRadar.module.css";

const SIZE = 220;
const CX = SIZE / 2;
const CY = SIZE / 2;
const MAX_R = 76;
const LABEL_R = MAX_R + 22;
const TICKS = [0.25, 0.5, 0.75, 1];

const AXES = MUSCLE_BUCKETS.map((b, i) => ({ ...b, angle: i * 60 }));

function polar(angleDeg, r) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function hexPoints(r) {
  return AXES.map(({ angle }) => {
    const { x, y } = polar(angle, r);
    return `${x},${y}`;
  }).join(" ");
}

export default function HexRadar({ buckets, prevBuckets }) {
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState(null);

  const data = buckets?.length
    ? buckets
    : MUSCLE_BUCKETS.map((b) => ({ ...b, setCount: 0 }));

  const prevData = prevBuckets?.length
    ? prevBuckets
    : MUSCLE_BUCKETS.map((b) => ({ ...b, setCount: 0 }));

  const prevTotal = prevData.reduce((s, d) => s + d.setCount, 0);

  // Scale both polygons against a shared max so they're directly comparable
  const maxVal = Math.max(...data.map((d) => d.setCount), ...prevData.map((d) => d.setCount), 1);
  const total = data.reduce((s, d) => s + d.setCount, 0);

  // Build a map from id → prevSetCount for delta computation
  const prevById = useMemo(() => new Map(prevData.map((b) => [b.id, b.setCount])), [prevData]);

  const dataPoints = useMemo(() => {
    return AXES.map(({ angle }, i) => {
      const r = (data[i].setCount / maxVal) * MAX_R;
      return `${polar(angle, r).x},${polar(angle, r).y}`;
    }).join(" ");
  }, [data, maxVal]);

  const prevPoints = useMemo(() => {
    return AXES.map(({ angle }, i) => {
      const r = (prevData[i].setCount / maxVal) * MAX_R;
      return `${polar(angle, r).x},${polar(angle, r).y}`;
    }).join(" ");
  }, [prevData, maxVal]);

  const handleEnter = (i, e) => {
    const { id, label } = AXES[i];
    const count = data[i].setCount;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    const prevCount = prevById.get(id) || 0;
    const delta = count - prevCount;
    const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
    setHoveredId(id);
    setTooltip({ label, count, pct, prevCount, delta, dir });
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleLeave = () => {
    setHoveredId(null);
    setTooltip(null);
  };

  return (
    <div id="muscle-radar-chart" className={styles.radar}>
      <div className={styles.chartWrap}>
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className={styles.svg} aria-hidden="true">
          {/* Tick hexagons — dim as a whole when hovered */}
          <g style={{ opacity: hoveredId ? 0.2 : 1 }}>
            {TICKS.map((t) => (
              <polygon
                key={t}
                points={hexPoints(MAX_R * t)}
                fill="none"
                stroke="var(--border-md)"
                strokeWidth="1"
              />
            ))}
          </g>

          {/* Previous-period polygon — behind spoke lines and current polygon */}
          {prevTotal > 0 && (
            <polygon
              points={prevPoints}
              fill="none"
              stroke="var(--muted)"
              strokeWidth="1"
              strokeDasharray="3 3"
              strokeLinejoin="round"
              style={{ opacity: hoveredId ? 0.15 : 0.5 }}
              className={styles.prevPolygon}
            />
          )}

          {/* Per-axis group: spoke line (data point → outer) + data point */}
          {AXES.map(({ angle, id }, i) => {
            const outer = polar(angle, MAX_R);
            const isHovered = hoveredId === id;
            const dimmed = hoveredId && !isHovered;
            const pt = polar(angle, (data[i].setCount / maxVal) * MAX_R);

            return (
              <g key={id} style={{ opacity: dimmed ? 0.15 : 1 }}>
                <line
                  x1={pt.x} y1={pt.y}
                  x2={outer.x} y2={outer.y}
                  stroke={isHovered ? "var(--amber)" : "var(--border-md)"}
                  strokeWidth={isHovered ? 1.5 : 1}
                />
                {data[i].setCount > 0 && (
                  <circle
                    cx={pt.x} cy={pt.y}
                    r={isHovered ? 5.5 : 3.5}
                    fill="var(--amber)"
                  />
                )}
              </g>
            );
          })}

          {/* Data polygon */}
          <polygon
            points={dataPoints}
            fill="rgba(217,126,36,0.18)"
            stroke="var(--amber)"
            strokeWidth="1.5"
            strokeLinejoin="round"
            style={{ opacity: hoveredId ? 0.35 : 1 }}
            className={styles.dataPolygon}
          />

          {/* Axis labels */}
          {AXES.map(({ angle, label, id }) => {
            const { x, y } = polar(angle, LABEL_R);
            const isHovered = hoveredId === id;
            const dimmed = hoveredId && !isHovered;
            return (
              <text
                key={id}
                x={x} y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                opacity={dimmed ? 0.3 : 1}
                className={`${styles.axisLabel} ${isHovered ? styles.axisLabelHovered : ""}`}
              >
                {label}
              </text>
            );
          })}

          {/* Invisible wedge hit areas — rendered last to sit on top */}
          {AXES.map(({ angle, id }, i) => {
            const a = polar(angle - 30, MAX_R + 30);
            const b = polar(angle + 30, MAX_R + 30);
            return (
              <polygon
                key={`hit-${id}`}
                points={`${CX},${CY} ${a.x},${a.y} ${b.x},${b.y}`}
                fill="transparent"
                style={{ cursor: "crosshair" }}
                onMouseEnter={(e) => handleEnter(i, e)}
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
              />
            );
          })}
        </svg>
      </div>

      <div className={styles.heading}>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.swatchCurrent} />
            Current
          </span>
          {prevTotal > 0 && (
            <span className={styles.legendItem}>
              <span className={styles.swatchPrev} />
              Previous
            </span>
          )}
        </div>
      </div>

      {tooltip && (
        <ChartTooltip className={styles.tooltip} x={tooltipPos.x} y={tooltipPos.y}>
          <span className={styles.tooltipLabel}>{tooltip.label}</span>
          <span className={styles.tooltipValue}>
            {tooltip.count > 0 ? `${tooltip.count} sets · ${tooltip.pct}%` : "No sets"}
          </span>
          {prevTotal > 0 && (
            <span className={`${styles.tooltipDelta} ${styles[tooltip.dir]}`}>
              {deltaSign(tooltip.dir)}{" "}
              {tooltip.delta !== 0
                ? `${tooltip.delta > 0 ? "+" : ""}${tooltip.delta} vs prev (${tooltip.prevCount} sets)`
                : `same as prev (${tooltip.prevCount} sets)`}
            </span>
          )}
        </ChartTooltip>
      )}
    </div>
  );
}
